import { useEffect, useState } from "react";
import { fetchPublicJobOpenings } from "../api/jobOpenings";
import {
  jobOpeningPath,
  mapStaticJobOpenings,
} from "../data/jobOpeningPublic";

// Dedupes concurrent identical requests (e.g. JobsNotice + CareersSection
// both mounting on the homepage) so they share one network call instead of
// each firing its own. Cleared once the request settles, so later mounts
// still get a fresh fetch.
const inFlight = new Map();

function loadPublicJobOpenings(params) {
  const key = JSON.stringify(params || {});
  if (!inFlight.has(key)) {
    const promise = fetchPublicJobOpenings(params).finally(() => {
      inFlight.delete(key);
    });
    inFlight.set(key, promise);
  }
  return inFlight.get(key);
}

export function usePublicJobOpenings(params = { per_page: 50 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await loadPublicJobOpenings(params);
        if (cancelled) return;
        if (Array.isArray(res?.data)) {
          const mapped = res.data.map((opening) => ({
            ...opening,
            path: jobOpeningPath(opening),
          }));
          setItems(mapped);
          setFromApi(true);
          setTotalCount(
            Number.isFinite(res?.totalCount) ? res.totalCount : mapped.length
          );
          return;
        }
        setItems(mapStaticJobOpenings());
        setFromApi(false);
        setTotalCount(0);
      } catch {
        if (!cancelled) {
          setItems(mapStaticJobOpenings());
          setFromApi(false);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // paramsKey is the stable, derived dependency — params itself is a new
    // object reference on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  return { items, loading, fromApi, totalCount };
}
